import type { VoxelField } from "@/core/field/VoxelField";

/**
 * Naive Surface Nets over the voxel field.
 *
 * One dual vertex per sign-changing cell, one quad per sign-changing sample
 * edge. That construction is closed by definition: every quad edge is shared
 * by exactly two quads, so the result is watertight before anything else
 * touches it. Two post-passes then give us the properties this product needs
 * and a plain isosurface would not:
 *
 *  1. FLAT REAR FACE. Every vertex in the cell layer straddling y = 0 is
 *     forced to exactly y = 0. The mounting surface is therefore planar to the
 *     last bit, whatever the user carved.
 *
 *  2. SHARP BLANK EDGES. A cell whose eight samples have never been touched by
 *     a brush still holds the exact analytic box distance, so we know its true
 *     surface is the block itself. Snapping those vertices onto the block's
 *     planes reproduces the blank's crisp edges and corners exactly, and keeps
 *     the exported bounding box at the advertised millimetre dimensions.
 */

export interface ExtractedMesh {
  positions: Float32Array;
  indices: Uint32Array;
  /**
   * Triangles from `coreMin`..`coreMax` come first. Chunked meshing emits a
   * one-cell halo so crease normals are correct at chunk seams, then keeps only
   * this many triangles for display.
   */
  coreTriangleCount?: number;
}

export interface ExtractOptions {
  /** Cell range to build vertices for (min-corner cell indices). */
  cellMin?: readonly [number, number, number];
  cellMax?: readonly [number, number, number];
  /** Cell range whose quads are emitted. Defaults to the vertex range. */
  emitMin?: readonly [number, number, number];
  emitMax?: readonly [number, number, number];
  /** Sub-range of the emit range whose quads are emitted first. */
  coreMin?: readonly [number, number, number];
  coreMax?: readonly [number, number, number];
}

const EDGE_CORNERS: ReadonlyArray<readonly [number, number]> = [
  [0, 1],
  [2, 3],
  [4, 5],
  [6, 7],
  [0, 2],
  [1, 3],
  [4, 6],
  [5, 7],
  [0, 4],
  [1, 5],
  [2, 6],
  [3, 7],
];

const CORNER_OFFSET: ReadonlyArray<readonly [number, number, number]> = [
  [0, 0, 0],
  [1, 0, 0],
  [0, 1, 0],
  [1, 1, 0],
  [0, 0, 1],
  [1, 0, 1],
  [0, 1, 1],
  [1, 1, 1],
];

/** Distance within which an untouched vertex is pulled onto a blank face. */
const SNAP_FACTOR = 0.75;

export function extractSurfaceNets(field: VoxelField, opts: ExtractOptions = {}): ExtractedMesh {
  const { nx, ny, nz, h, ox, oy, oz, data, dirty } = field;

  const vMin: [number, number, number] = [
    Math.max(0, opts.cellMin?.[0] ?? 0),
    Math.max(0, opts.cellMin?.[1] ?? 0),
    Math.max(0, opts.cellMin?.[2] ?? 0),
  ];
  const vMax: [number, number, number] = [
    Math.min(nx - 1, opts.cellMax?.[0] ?? nx - 1),
    Math.min(ny - 1, opts.cellMax?.[1] ?? ny - 1),
    Math.min(nz - 1, opts.cellMax?.[2] ?? nz - 1),
  ];
  const eMin: [number, number, number] = [
    opts.emitMin?.[0] ?? vMin[0],
    opts.emitMin?.[1] ?? vMin[1],
    opts.emitMin?.[2] ?? vMin[2],
  ];
  const eMax: [number, number, number] = [
    opts.emitMax?.[0] ?? vMax[0],
    opts.emitMax?.[1] ?? vMax[1],
    opts.emitMax?.[2] ?? vMax[2],
  ];

  const sx = Math.max(0, vMax[0] - vMin[0]);
  const sy = Math.max(0, vMax[1] - vMin[1]);
  const sz = Math.max(0, vMax[2] - vMin[2]);
  if (sx <= 0 || sy <= 0 || sz <= 0) {
    return { positions: new Float32Array(0), indices: new Uint32Array(0), coreTriangleCount: 0 };
  }

  const vertexOf = new Int32Array(sx * sy * sz).fill(-1);
  const cellIndex = (ci: number, cj: number, ck: number): number =>
    (ci - vMin[0]) + sx * (cj - vMin[1] + sy * (ck - vMin[2]));

  const positions: number[] = [];
  const baseJ = field.baseCellJ;
  const snapEps = h * SNAP_FACTOR;
  const { width, height, depth } = field;

  const corner = new Float64Array(8);

  for (let ck = vMin[2]; ck < vMax[2]; ck++) {
    for (let cj = vMin[1]; cj < vMax[1]; cj++) {
      for (let ci = vMin[0]; ci < vMax[0]; ci++) {
        let mask = 0;
        let clean = true;
        for (let c = 0; c < 8; c++) {
          const off = CORNER_OFFSET[c] as readonly [number, number, number];
          const p = field.index(ci + off[0], cj + off[1], ck + off[2]);
          const v = data[p] as number;
          corner[c] = v;
          if (v < 0) mask |= 1 << c;
          if ((dirty[p] as number) !== 0) clean = false;
        }
        if (mask === 0 || mask === 255) continue;

        let ux = 0;
        let uy = 0;
        let uz = 0;
        let n = 0;
        for (let e = 0; e < 12; e++) {
          const pair = EDGE_CORNERS[e] as readonly [number, number];
          const a = pair[0];
          const b = pair[1];
          const inA = (mask >> a) & 1;
          const inB = (mask >> b) & 1;
          if (inA === inB) continue;
          const va = corner[a] as number;
          const vb = corner[b] as number;
          const den = va - vb;
          const t = Math.abs(den) < 1e-12 ? 0.5 : va / den;
          const oa = CORNER_OFFSET[a] as readonly [number, number, number];
          const ob = CORNER_OFFSET[b] as readonly [number, number, number];
          ux += oa[0] + (ob[0] - oa[0]) * t;
          uy += oa[1] + (ob[1] - oa[1]) * t;
          uz += oa[2] + (ob[2] - oa[2]) * t;
          n++;
        }
        if (n === 0) continue;

        let px = ox + (ci + ux / n) * h;
        let py = oy + (cj + uy / n) * h;
        let pz = oz + (ck + uz / n) * h;

        if (clean) {
          // Untouched foam: the true surface here IS the blank, so restore the
          // block's exact planes, edges and corners.
          if (Math.abs(px) < snapEps) px = 0;
          else if (Math.abs(px - width) < snapEps) px = width;
          if (Math.abs(py) < snapEps) py = 0;
          else if (Math.abs(py - height) < snapEps) py = height;
          if (Math.abs(pz) < snapEps) pz = 0;
          else if (Math.abs(pz - depth) < snapEps) pz = depth;
        }

        // The mounting plane is absolute.
        if (cj === baseJ) py = 0;
        else if (py < 0) py = 0;
        if (py > height) py = height;
        if (px < field.minX) px = field.minX;
        else if (px > field.maxX) px = field.maxX;
        if (pz < field.minZ) pz = field.minZ;
        else if (pz > field.maxZ) pz = field.maxZ;

        vertexOf[cellIndex(ci, cj, ck)] = positions.length / 3;
        positions.push(px, py, pz);
      }
    }
  }

  const coreIndices: number[] = [];
  const haloIndices: number[] = [];
  const hasCore = opts.coreMin !== undefined && opts.coreMax !== undefined;
  const cMin = opts.coreMin ?? eMin;
  const cMax = opts.coreMax ?? eMax;
  let target = coreIndices;
  const quad = (a: number, b: number, c: number, d: number, flip: boolean): void => {
    if (a < 0 || b < 0 || c < 0 || d < 0) return;
    if (flip) {
      target.push(a, c, b, a, d, c);
    } else {
      target.push(a, b, c, a, c, d);
    }
  };
  const route = (ci: number, cj: number, ck: number): void => {
    const core =
      !hasCore ||
      (ci >= (cMin[0] as number) &&
        ci < (cMax[0] as number) &&
        cj >= (cMin[1] as number) &&
        cj < (cMax[1] as number) &&
        ck >= (cMin[2] as number) &&
        ck < (cMax[2] as number));
    target = core ? coreIndices : haloIndices;
  };
  const inEmit = (ci: number, cj: number, ck: number): boolean =>
    ci >= eMin[0] && ci < eMax[0] && cj >= eMin[1] && cj < eMax[1] && ck >= eMin[2] && ck < eMax[2];
  const vertAt = (ci: number, cj: number, ck: number): number => {
    if (ci < vMin[0] || cj < vMin[1] || ck < vMin[2]) return -1;
    if (ci >= vMax[0] || cj >= vMax[1] || ck >= vMax[2]) return -1;
    return vertexOf[cellIndex(ci, cj, ck)] as number;
  };

  // A quad is owned by the lowest-indexed of the four cells around its sample
  // edge, so chunked extraction emits every quad exactly once.
  for (let k = vMin[2]; k <= vMax[2]; k++) {
    for (let j = vMin[1]; j <= vMax[1]; j++) {
      for (let i = vMin[0]; i <= vMax[0]; i++) {
        const here = data[field.index(i, j, k)] as number;
        const inHere = here < 0;

        if (i + 1 < nx) {
          const inNext = (data[field.index(i + 1, j, k)] as number) < 0;
          if (inHere !== inNext && inEmit(i, j - 1, k - 1)) {
            route(i, j - 1, k - 1);
            quad(
              vertAt(i, j - 1, k - 1),
              vertAt(i, j, k - 1),
              vertAt(i, j, k),
              vertAt(i, j - 1, k),
              !inHere,
            );
          }
        }
        if (j + 1 < ny) {
          const inNext = (data[field.index(i, j + 1, k)] as number) < 0;
          if (inHere !== inNext && inEmit(i - 1, j, k - 1)) {
            route(i - 1, j, k - 1);
            quad(
              vertAt(i - 1, j, k - 1),
              vertAt(i - 1, j, k),
              vertAt(i, j, k),
              vertAt(i, j, k - 1),
              !inHere,
            );
          }
        }
        if (k + 1 < nz) {
          const inNext = (data[field.index(i, j, k + 1)] as number) < 0;
          if (inHere !== inNext && inEmit(i - 1, j - 1, k)) {
            route(i - 1, j - 1, k);
            quad(
              vertAt(i - 1, j - 1, k),
              vertAt(i, j - 1, k),
              vertAt(i, j, k),
              vertAt(i - 1, j, k),
              !inHere,
            );
          }
        }
      }
    }
  }

  const indices = new Uint32Array(coreIndices.length + haloIndices.length);
  indices.set(coreIndices, 0);
  indices.set(haloIndices, coreIndices.length);

  return {
    positions: new Float32Array(positions),
    indices,
    coreTriangleCount: coreIndices.length / 3,
  };
}
